import Reveal from "@/components/base/Reveal";
import { chefBio, cookingTips, externalLinks, images, photos, roles } from "@/mocks/site";

const gallery = [images.chefTable, images.kitchenCrew, images.marina, photos.coupleTravel];

export default function ChefSection() {
  return (
    <section className="bg-background-100">
      <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
        <Reveal>
          <p className="eyebrow">Section 01 — Chef Eddie G</p>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal variant="mask">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-foreground-900 image-zoom">
                <img
                  src={images.chefPortrait}
                  alt="Chef Eddie G"
                  title="Chef Eddie G — the person behind the journey"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="mt-4 flex items-center justify-between">
                <span className="font-label text-[10px] uppercase tracking-label text-foreground-500">
                  On the road
                </span>
                <span className="font-heading text-sm italic text-foreground-500">
                  Chef Eddie G
                </span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-heading text-4xl leading-[1.02] text-foreground-950 sm:text-5xl md:text-6xl">
                THE CHEF
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <div className="mt-6 h-px w-16 bg-primary-500" />
            </Reveal>
            <Reveal delay={180}>
              <p className="mt-6 font-body text-lg leading-relaxed text-foreground-700">
                Chef Eddie G is a chef, storyteller, traveler and connector
                whose work has always been about more than the plate. He cooks,
                he travels, and he gathers people — following food to the
                places and communities where it means the most.
              </p>
            </Reveal>
            <Reveal delay={260}>
              <p className="mt-4 font-body text-base leading-relaxed text-foreground-700">
                From a crowded kitchen line to a long table overseas, the
                through-line is the same: food is the way we find each other.
              </p>
            </Reveal>

            <Reveal delay={320}>
              <p className="mt-6 font-body text-base leading-relaxed text-foreground-700">
                {chefBio.short}
              </p>
            </Reveal>

            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-4">
              {roles.map((role, i) => (
                <Reveal key={role.title} delay={340 + i * 70}>
                  <div>
                    <div className="font-heading text-2xl text-primary-600 md:text-3xl">
                      {role.title}
                    </div>
                    <div className="mt-2 font-body text-sm italic text-foreground-600">
                      {role.note}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={620}>
              <a
                href={externalLinks.chefEddieG}
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline mt-11 inline-flex w-fit items-center gap-2 font-label text-[11px] uppercase tracking-label text-primary-700"
              >
                Visit chefeddieg.org
                <i className="ri-arrow-right-up-line text-base" />
              </a>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {gallery.map((src, i) => (
            <Reveal key={src} delay={i * 90} variant="mask">
              <div className="aspect-[3/4] w-full overflow-hidden bg-foreground-900 image-zoom">
                <img
                  src={src}
                  alt="From the world of Chef Eddie G"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow">Video Feature</p>
            </Reveal>
            <Reveal delay={120}>
              <div className="mt-6 aspect-video w-full overflow-hidden bg-foreground-950">
                <iframe
                  src="https://www.youtube.com/embed/r2YJDRsHnmI"
                  title="Chef Eddie G — in the kitchen"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow">From the Kitchen</p>
            </Reveal>
            <Reveal delay={120}>
              <h3 className="mt-5 font-heading text-2xl text-foreground-950 md:text-3xl">
                Cooking tips, learned the long way
              </h3>
            </Reveal>
            <div className="mt-8 divide-y divide-foreground-950/12 border-t border-foreground-950/12">
              {cookingTips.map((tip, i) => (
                <Reveal key={tip.no} delay={180 + i * 80}>
                  <div className="flex gap-5 py-5">
                    <span className="font-heading text-lg italic text-primary-500">
                      {tip.no}
                    </span>
                    <div>
                      <h4 className="font-heading text-lg text-foreground-950">
                        {tip.title}
                      </h4>
                      <p className="mt-1 font-body text-sm leading-relaxed text-foreground-600">
                        {tip.note}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}